<template>
  <div class="playground-page min-h-screen bg-gray-50 dark:bg-gray-900">
    <div class="container mx-auto px-4 py-6">
      <!-- Header -->
      <div class="mb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">API Playground</h1>
            <p class="text-gray-600 dark:text-gray-400">Test AI models interactively</p>
          </div>

          <!-- Status Bar -->
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <Badge v-if="isAuthenticated" variant="success" size="sm">Authenticated</Badge>
              <Badge v-else variant="danger" size="sm">Not Authenticated</Badge>
            </div>

            <div class="flex items-center gap-2">
              <LoadingSpinner v-if="isLoadingModels" size="sm" />
              <Badge v-else-if="cachedModels.length > 0" variant="info" size="sm">
                {{ cachedModels.length }} models
              </Badge>
              <Button
                v-if="!isLoadingModels"
                variant="ghost"
                size="sm"
                @click="refreshModels"
                title="Refresh models"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </Button>
            </div>

            <Button
              variant="ghost"
              size="sm"
              @click="handleClearSession"
              title="Clear session (Ctrl+K)"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </Button>
          </div>
        </div>

        <!-- Error Display -->
        <Alert v-if="modelsError" variant="error" class="mt-4">
          {{ modelsError }}
        </Alert>
      </div>

      <!-- Playground Tabs -->
      <Card padding="none" class="overflow-hidden">
        <PlaygroundTabs :active-tab="activeTab" @tab-change="handleTabChange" />

        <!-- Playground Content -->
        <div class="p-0">
          <!-- Text Playground -->
          <TextPlayground
            v-if="activeTab === 'text'"
            :models="textModels"
            :is-loading-models="isLoadingModels"
          />

          <!-- Image Playground (generation) -->
          <ImagePlayground
            v-if="activeTab === 'vision'"
            :models="imageGenerationModels"
            :is-loading-models="isLoadingModels"
          />

          <!-- Audio Playground -->
          <AudioPlayground
            v-if="activeTab === 'audio'"
            :tts-models="ttsModels"
            :stt-models="sttModels"
            :is-loading-models="isLoadingModels"
          />

          <!-- Embeddings Playground -->
          <EmbeddingsPlayground
            v-if="activeTab === 'embeddings'"
            :models="embeddingsModels"
            :is-loading-models="isLoadingModels"
          />

          <!-- Moderation Playground -->
          <ModerationPlayground
            v-if="activeTab === 'moderation'"
            :models="moderationModels"
            :is-loading-models="isLoadingModels"
          />

          <!-- Video Playground -->
          <VideoPlayground v-if="activeTab === 'video'" />
        </div>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, watch } from 'vue';
  import { Alert, Badge, Button, LoadingSpinner, Card } from '@asabdev/asab-ui';
  import { useAuthStore } from '@/stores/auth';
  import { useSessionStorage, useModelsCache } from '../composables';
  import { PlaygroundTabs } from '../components/shared';
  import { TextPlayground } from '../components/text';
  import ImagePlayground from '../components/image/ImagePlayground.vue';
  import { AudioPlayground } from '../components/audio';
  import { EmbeddingsPlayground } from '../components/embeddings';
  import { ModerationPlayground } from '../components/moderation';
  import { VideoPlayground } from '../components/video';
  import type { PlaygroundTab } from '../types/session';

  // Stores
  const authStore = useAuthStore();

  // Session storage
  const { session, loadSession, saveSession, clearSession, createEmptySession } =
    useSessionStorage();

  // Models cache
  const {
    isLoading: isLoadingModels,
    error: modelsError,
    cachedModels,
    textModels,
    imageGenerationModels,
    ttsModels,
    sttModels,
    embeddingsModels,
    moderationModels,
    loadModels,
    clearCache,
  } = useModelsCache();

  // State
  const activeTab = ref<PlaygroundTab>('text');

  // Computed
  const isAuthenticated = computed(() => !!authStore.token);

  // Methods
  const handleTabChange = (tab: PlaygroundTab) => {
    activeTab.value = tab;
    updateSession();
  };

  const handleClearSession = () => {
    if (confirm('Clear all playground data? This cannot be undone.')) {
      clearSession();
      clearCache();
      activeTab.value = 'text';
      session.value = createEmptySession();
      saveSession(session.value);
    }
  };

  const refreshModels = async () => {
    await loadModels(true);
  };

  const updateSession = () => {
    if (!session.value) {
      session.value = createEmptySession();
    }
    session.value.activeTab = activeTab.value;
    session.value.timestamp = new Date();
    saveSession(session.value);
  };

  const restoreSession = () => {
    const loaded = loadSession();
    if (loaded) {
      if (loaded.activeTab) {
        activeTab.value = loaded.activeTab;
      }
    } else {
      session.value = createEmptySession();
      saveSession(session.value);
    }
  };

  // Lifecycle
  onMounted(async () => {
    restoreSession();

    // Auto-load models on mount if authenticated
    if (isAuthenticated.value) {
      await loadModels();
    }
  });

  // Watch for auth changes
  watch(
    () => authStore.token,
    async newToken => {
      if (newToken) {
        await loadModels(true);
      } else {
        clearCache();
      }
    }
  );
</script>

<style scoped>
  .playground-page {
    min-height: calc(100vh - 4rem);
  }
</style>
